import type { RoomFinishSettings, WallFinishId } from "../types";
import { mix, shade, withAlpha } from "../furniture/color";
import { wallFinish } from "./catalog";

/**
 * The quiet 2D echo of chosen finishes: a faint floor tint on the plan, and
 * the wall's color with hairline boards, slats or courses in the Walls
 * elevation. Never textures, and only what the user chose: inferred
 * finishes leave 2D exactly as it was.
 */

/** A hairline in wall inches: x along the wall, y up from the floor. */
export type WallSegment2d = [x0: number, y0: number, x1: number, y1: number];

export interface ElevationWallLook {
  fill: string;
  /** Hairline stroke for the pattern. */
  line: string;
  /** How high the finish goes (a fence stops short of the wall top), inches. */
  top: number;
  segments: WallSegment2d[];
}

/** Lines closer than this on screen read as noise; that family is left out. */
const MIN_SPACING_PX = 5;
const MAX_SEGMENTS = 600;

/** Plan floor fill: a tenth of the chosen floor color over white. */
export function planFloorFill(finishes: RoomFinishSettings | undefined): string | null {
  const floor = finishes?.floor;
  return floor ? mix("#ffffff", floor.color, 0.1) : null;
}

/** The elevation look for one wall (`key` from `wallKeys.ts`), or null when nothing was chosen for it. */
export function elevationWallLook(
  finishes: RoomFinishSettings | undefined,
  key: string | null,
  width: number,
  height: number,
  pxPerIn: number
): ElevationWallLook | null {
  const f = (key ? finishes?.wallOverrides?.[key] : undefined) ?? finishes?.walls;
  if (!f) return null;
  const option = wallFinish(f.type);
  const top = Math.min(height, option.height ?? height);
  return {
    fill: mix("#ffffff", f.color, 0.4),
    line: withAlpha(shade(f.color, -0.45), 0.4),
    top,
    segments: wallPattern(f.type, width, top, pxPerIn),
  };
}

/** Hairlines that say what a wall is made of, within `width` × `top` inches. */
export function wallPattern(type: WallFinishId, width: number, top: number, pxPerIn: number): WallSegment2d[] {
  const out: WallSegment2d[] = [];
  const legible = (step: number) => step * pxPerIn >= MIN_SPACING_PX;
  const rows = (step: number, upTo = top) => {
    if (!legible(step)) return;
    for (let y = step; y < upTo - 0.01 && out.length < MAX_SEGMENTS; y += step) out.push([0, y, width, y]);
  };
  const cols = (step: number, upTo = top) => {
    if (!legible(step)) return;
    for (let x = step; x < width - 0.01 && out.length < MAX_SEGMENTS; x += step) out.push([x, 0, x, upTo]);
  };
  switch (type) {
    case "shiplap":
      rows(6);
      break;
    case "slat":
      cols(3);
      break;
    case "panelling": {
      const rail = Math.min(36, top * 0.4);
      out.push([0, rail, width, rail]);
      cols(16, rail);
      break;
    }
    case "tile":
      rows(6);
      cols(6);
      break;
    case "brick":
    case "masonry":
      rows(8 / 3);
      break;
    case "fence-wood":
      cols(6);
      break;
    case "fence-slat":
      rows(4);
      break;
    case "railing":
      out.push([0, top - 2, width, top - 2]);
      cols(4);
      break;
    case "house":
      rows(8);
      break;
    default:
      break;
  }
  return out;
}

/** One SVG path for many hairlines (a single Konva node instead of hundreds). */
export function segmentsPath(segments: WallSegment2d[], toX: (x: number) => number, toY: (y: number) => number): string {
  let d = "";
  for (const [x0, y0, x1, y1] of segments) {
    d += `M${toX(x0).toFixed(1)} ${toY(y0).toFixed(1)}L${toX(x1).toFixed(1)} ${toY(y1).toFixed(1)}`;
  }
  return d;
}
